import { useStateXGetter } from '@cloudio-saas/statex';
import { runtimePath } from 'designer/atoms';
import React, { memo, CSSProperties, useRef, useEffect } from 'react';
import { useScroll, useScrollValue } from './GridScroll';

interface Props {
  style: CSSProperties;
  height: number;
  width: number;
  horizontal: boolean;
  scrollToRow: number;
  scrollToColumn: number;
  rowHeight: number;
  bodyHeight: number;
  bodyWidth: number;
  columnLefts: number[];
  columnWidths: number[];
  appUid: string;
  pageId: string;
  itemId: string;
}

function Scrollbar({
  style,
  width,
  height,
  horizontal,
  scrollToRow,
  scrollToColumn,
  rowHeight,
  bodyHeight,
  bodyWidth,
  columnLefts,
  columnWidths,
  appUid,
  pageId,
  itemId,
}: Props): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const get = useStateXGetter();
  useScroll(ref, horizontal);
  const initTopRef = useRef(false);
  const initLeftRef = useRef(false);
  const mv = useScrollValue();
  useEffect(() => {
    if (ref.current) {
      const top =
        get<number>(runtimePath({ appUid, pageId, itemId, key: 'top' })) ?? 0;
      const left =
        get<number>(runtimePath({ appUid, pageId, itemId, key: 'left' })) ?? 0;
      if (top || left) {
        // reset initial scroll position
        if (!horizontal) {
          if (bodyHeight <= 0 || initTopRef.current) return; // wait till the body is rendered
          initTopRef.current = true;
          if (ref.current) ref.current.scrollTop = top;
        } else {
          if (initLeftRef.current) return;
          initLeftRef.current = true;
          ref.current.scrollLeft = left;
        }
      }
    }
  }, [appUid, bodyHeight, get, horizontal, itemId, pageId]);
  useEffect(() => {
    return mv.pubsub.sub('UpdateScroll', async (_event, { x, y }) => {
      if (ref.current) {
        if (horizontal) {
          ref.current.scrollLeft = -x;
        } else {
          ref.current.scrollTop = -y;
        }
      }
    });
  }, [horizontal, mv]);
  useEffect(() => {
    if (ref.current) {
      if (!horizontal && scrollToRow) {
        let top = scrollToRow * rowHeight;
        if (top > height - bodyHeight) {
          top = height - bodyHeight;
        }

        if (
          top >= ref.current.scrollTop &&
          top < ref.current.scrollTop + bodyHeight - rowHeight
        ) {
          // ignore. it's already in view
        } else if (top < ref.current.scrollTop) {
          // is above. hence bring it to top
          ref.current.scrollTop = top;
        } else {
          // is below. hence bring it to bottom
          ref.current.scrollTop = top - bodyHeight + rowHeight;
        }
      } else if (horizontal && scrollToColumn) {
        const left = columnLefts[scrollToColumn];
        if (
          left >= ref.current.scrollLeft &&
          left <
            ref.current.scrollLeft + bodyWidth - columnWidths[scrollToColumn]
        ) {
          // ignore. it's already in view
        } else if (left < ref.current.scrollLeft) {
          // is left. hence bring it to left end
          ref.current.scrollLeft = left;
        } else {
          // is right. hence bring it to right end
          ref.current.scrollLeft =
            left - bodyWidth + columnWidths[scrollToColumn];
        }
      }
    }
  }, [
    bodyHeight,
    bodyWidth,
    columnLefts,
    columnWidths,
    height,
    horizontal,
    rowHeight,
    scrollToColumn,
    scrollToRow,
  ]);
  return (
    <div ref={ref} style={style}>
      <div style={{ width, height }} />
    </div>
  );
}

export default memo(Scrollbar);
